// The billing history's search params (guide, rule 16), read the same way by the portal's /billing
// and the staff's reseller page: a link to "October's debits" opens just that. With no range in the
// URL the history shows this month, a month of WIB calendar days (D10), so the range is never empty.
// Anything the API would refuse reads as not there, and a range given backwards is read the right
// way round, so a typo in a date never lands on an error page.
import { TOP_UP_LIST_STATES } from "@kilat/contracts/top-up";
import { WALLET_ENTRY_KINDS } from "@kilat/contracts/wallet";
import { z } from "zod";

const WIB_OFFSET_MS = 7 * 3600_000;

const EntriesSearch = z.object({
  kind: z.enum(WALLET_ENTRY_KINDS).optional().catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});

const day = (date: Date) => date.toISOString().slice(0, 10);

/** The WIB calendar month `now` falls in, as its first and its last day. */
export function currentMonth(now = new Date()) {
  const wib = new Date(now.getTime() + WIB_OFFSET_MS);
  const year = wib.getUTCFullYear();
  const month = wib.getUTCMonth();
  return { from: day(new Date(Date.UTC(year, month, 1))), to: day(new Date(Date.UTC(year, month + 1, 0))) };
}

/** A route's validateSearch for the billing history. */
export function readEntriesSearch(raw: Record<string, unknown>, now = new Date()) {
  const search = EntriesSearch.parse(raw);
  const { from, to } = search;
  if (!from && !to) return { ...search, ...currentMonth(now) };
  return from && to && from > to ? { ...search, from: to, to: from } : search;
}

const TopUpsSearch = z.object({
  state: z.enum(TOP_UP_LIST_STATES).optional().catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});

/**
 * The top-ups tab's validateSearch. Unlike the history it has no default month: with no range
 * every top-up shows, so a pending one or one under review from an earlier month is not hidden.
 */
export function readTopUpsSearch(raw: Record<string, unknown>) {
  const search = TopUpsSearch.parse(raw);
  const { from, to } = search;
  return from && to && from > to ? { ...search, from: to, to: from } : search;
}
